import { Controller, FieldPath, useFormContext } from 'react-hook-form';
import { RegisterOptions } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { get } from 'lodash';
import { CircleXIcon } from 'lucide-react';

import { DatePicker } from 'components/DatePicker';
import InputPreviousValue from 'components/InputPreviousValue';
import { Label } from 'components/Label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import useTMSContext from 'hooks/useTMSContext';
import { NormalizedLoad } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { initFieldAttributes } from 'types/LoadAttributes';
import { FieldAttributes } from 'types/LoadAttributes';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { normalizeTimezone } from 'utils/normalizeTimezone';
import { parseToUtcWallClockDate } from 'utils/parseToUtcWallClockDate';
import { getShortTimezoneAbbreviation } from 'utils/time';

dayjs.extend(utc);
dayjs.extend(timezone);

type LoadDateInputProps = Omit<
  React.ComponentPropsWithoutRef<typeof RHFTextInput>,
  'prevValue'
> & {
  name: FieldPath<NormalizedLoad>;
  showAIHint?: boolean;
  prevValue?: Undef<Date>;
  load?: NormalizedLoad;
  forceEditable?: boolean;
  rules?: Omit<
    RegisterOptions<NormalizedLoad, FieldPath<NormalizedLoad>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  /** When true, values are UTC wall-clock datetimes (see parseToUtcWallClockDate). */
  utcWallClock?: boolean;
  /**
   * IANA timezone (e.g. stop location) used only for the label suffix, e.g. "(CDT)".
   * Storage and calendar remain UTC wall-clock; label suffix only when a stop TZ is set.
   */
  displayTimezone?: MaybeUndef<string>;
  disablePastDates?: boolean;
  readOnly?: boolean;
};

const UTC_TZ = 'UTC';

function toUtcMidnightFromPickerDate(date: Date): Date {
  const ymd = dayjs(date).tz(UTC_TZ).format('YYYY-MM-DD');
  return dayjs.utc(ymd, 'YYYY-MM-DD').startOf('day').toDate();
}

export const LoadDateInput = ({
  name,
  label,
  prevValue,
  required,
  rules,
  forceEditable = false,
  utcWallClock = false,
  disablePastDates = true,
  readOnly = false,
  options: fieldOptions,
  displayTimezone,
}: LoadDateInputProps) => {
  const { tmsName } = useTMSContext();
  const normalizedDisplayTz = displayTimezone
    ? normalizeTimezone(displayTimezone) || displayTimezone
    : undefined;
  const shortDisplayTz = normalizedDisplayTz
    ? getShortTimezoneAbbreviation(normalizedDisplayTz, tmsName as TMS)
    : '';

  const extendedFormContext = useExtendedFormContext();
  const formContext = useFormContext<NormalizedLoad>();
  const {
    control: formControl,
    formState: { dirtyFields, errors },
  } = formContext;

  // Handle date selection for legacy (non-UTC wall-clock) dates
  // Currently used for Turvo, ThreeG, and GlobalTranzTMS
  const handleDateSelectLegacy = (date: Maybe<Date>, field: any) => {
    if (typeof field.onChange === 'function') {
      const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : null;
      field.onChange(formattedDate);
    }
  };

  const handleDateSelectUtc = (date: Maybe<Date>, field: any) => {
    if (typeof field.onChange !== 'function') return;
    if (!date) {
      field.onChange(null);
      return;
    }
    field.onChange(toUtcMidnightFromPickerDate(date));
  };

  const highlightDirtyField =
    !!extendedFormContext &&
    (() => {
      const highlightDirtyFields =
        extendedFormContext.highlightDirtyFields ?? false;
      const fieldState = formContext.getFieldState(name, formContext.formState);
      const isDirty = fieldState.isDirty || get(dirtyFields, name);

      return Boolean(isDirty && highlightDirtyFields);
    })();

  const allFieldAttrs = useFieldAttributes();

  const getFieldAttrForName = (fieldName: string): FieldAttributes => {
    let fieldAttr = getFieldAttribute(allFieldAttrs, fieldName);

    if (fieldName.startsWith('stops.')) {
      const parts = fieldName.split('.');

      if (parts.length >= 3 && !isNaN(Number(parts[1]))) {
        const fieldNameWithoutIndex = `${parts[0]}.${parts.slice(2).join('.')}`;
        fieldAttr = getFieldAttribute(allFieldAttrs, fieldNameWithoutIndex);
      }
    }

    return fieldAttr ?? initFieldAttributes;
  };

  const thisFieldAttr: FieldAttributes = getFieldAttrForName(name);
  const effectiveFieldAttr = forceEditable
    ? { ...thisFieldAttr, isReadOnly: false }
    : thisFieldAttr;
  const isReadOnlyField = readOnly || effectiveFieldAttr.isReadOnly;

  const controllerRules = {
    ...fieldOptions,
    ...rules,
    ...(required ? { required: 'Required' as const } : {}),
  } as RegisterOptions<NormalizedLoad, FieldPath<NormalizedLoad>>;

  let displayLabel = label;
  if (utcWallClock && shortDisplayTz) {
    displayLabel = `${label} (${shortDisplayTz})`;
  }

  let prevValueStr = '';
  if (prevValue) {
    prevValueStr = utcWallClock
      ? dayjs.utc(prevValue).format('M/DD/YYYY')
      : dayjs(prevValue).format('M/DD/YYYY');
  }

  const disabledDatesUtc = (date: Date) => {
    const todayStart = dayjs.utc().startOf('day');
    const candidate = dayjs(date).tz(UTC_TZ).startOf('day');
    return candidate.isBefore(todayStart);
  };

  if (effectiveFieldAttr.isNotSupported) {
    return null;
  }

  if (utcWallClock) {
    return (
      <div className='w-full'>
        <Label name={name} required={required}>
          {displayLabel}
        </Label>

        <Controller
          name={name}
          control={formControl}
          rules={controllerRules}
          render={({ field }) => {
            let dateValue: Maybe<Date> = null;
            if (field.value) {
              if (field.value instanceof Date) {
                dateValue = field.value;
              } else if (typeof field.value === 'string') {
                dateValue = parseToUtcWallClockDate(field.value);
              }
            }

            return (
              <TooltipProvider>
                <Flex gap='sm' className='mt-1' data-name={name}>
                  <DatePicker
                    logProperties={{ dateLabel: label }}
                    field={{
                      ...field,
                      value: dateValue,
                      onChange: (date: Maybe<Date>) =>
                        handleDateSelectUtc(date, field),
                    }}
                    thisFieldAttr={effectiveFieldAttr}
                    highlightDirtyField={highlightDirtyField}
                    timezone={UTC_TZ}
                    disabled={disablePastDates ? disabledDatesUtc : undefined}
                    readOnly={isReadOnlyField}
                  />
                  {field.value && !isReadOnlyField && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          title='Clear date'
                          type='button'
                          onClick={() => field.onChange(null)}
                          className='h-9 flex items-center justify-center'
                        >
                          <CircleXIcon className='h-4 w-4' />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Clear date</TooltipContent>
                    </Tooltip>
                  )}
                </Flex>
              </TooltipProvider>
            );
          }}
        />

        {prevValue ? <InputPreviousValue prevValue={prevValueStr} /> : null}

        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>
    );
  }

  return (
    <div className='w-full'>
      <Label name={name} required={required}>
        {label}
      </Label>

      <Controller
        name={name}
        control={formControl}
        rules={controllerRules}
        render={({ field }) => {
          let dateValue: Maybe<Date> = null;

          if (field.value) {
            if (field.value instanceof Date) {
              const dateOnly = field.value.toISOString().split('T')[0];
              dateValue = dayjs(dateOnly).toDate();
            } else if (typeof field.value === 'string') {
              if (field.value.includes('T')) {
                const dateOnly = field.value.split('T')[0];
                dateValue = dayjs(dateOnly).toDate();
              } else {
                dateValue = dayjs(field.value).toDate();
              }
            }
          }

          return (
            <TooltipProvider>
              <Flex gap='sm' className='mt-1' data-name={name}>
                <DatePicker
                  logProperties={{ dateLabel: label }}
                  field={{
                    ...field,
                    value: dateValue,
                    onChange: (date: Maybe<Date>) =>
                      handleDateSelectLegacy(date, field),
                  }}
                  thisFieldAttr={effectiveFieldAttr}
                  highlightDirtyField={highlightDirtyField}
                  readOnly={isReadOnlyField}
                />
                {field.value && !isReadOnlyField && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        title='Clear date'
                        type='button'
                        onClick={() => field.onChange(null)}
                        className='h-9 flex items-center justify-center'
                      >
                        <CircleXIcon className='w-4 h-4' />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Clear date</TooltipContent>
                  </Tooltip>
                )}
              </Flex>
            </TooltipProvider>
          );
        }}
      />

      {prevValue ? <InputPreviousValue prevValue={prevValueStr} /> : null}

      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }: { message: string }) => (
          <Typography variant='body-xs' className='text-error-500'>
            {message}
          </Typography>
        )}
      />
    </div>
  );
};
